<template>
  <div class="theme-select">
    <div class="theme-content" v-if="!isPresentation">
      <div class="theme-search">
        <input
          placeholder="Search Theme"
          type="text"
          class="theme-search__input"
          v-model="searchQuery"
        />
        <span class="theme-search__clear" @click="clearSearch">Clear</span>
      </div>
      <div class="theme-select__layouts">
        <LayoutItem
          v-for="layout in layouts"
          class="theme-select__layout"
          :key="layout.type"
          :label="layout.label"
          :type="layout.type"
          :isActive="activeLayout === layout.type"
          :onClick="() => onLayoutClick(layout.type)"
        />
      </div>

      <div class="list">
        <Loader v-if="themesLoading" />
        <div
          @click="() => selectTheme(themeId)"
          v-for="themeId in this.filteredThemes"
          :key="themeId"
          class="theme-item"
        >
          <div
            :class="{
              'theme-back': true,
              'theme-back-active': theme === themeId,
            }"
          >
            <div class="theme">
              <div
                :class="{
                  circle: true,
                  leftBorder: themesByName[themeId].brandColor === '#fff',
                  rightBorder: themesByName[themeId].primaryColor === '#fff',
                }"
              >
                <div class="border"></div>
                <div
                  class="half left"
                  :style="{
                    background: themesByName[themeId].brandColor,
                  }"
                />
                <div
                  class="half right"
                  :style="{
                    background: themesByName[themeId].primaryColor,
                  }"
                />
              </div>
            </div>
          </div>
          <div class="theme-name">
            <span>{{ themesByName[themeId].title }}</span>
            <span
              v-if="parseInt(themesByName[themeId].version, 10) === 2"
              class="theme-name-label"
              >2.0</span
            >
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isPresentation"
      :class="{ list: true, 'list--presentation': isPresentation }"
    >
      <Loader v-if="themesLoading" />
      <div
        @click="() => selectTheme(themeId)"
        v-for="themeId in this.filteredThemes.slice(0, 7)"
        v-bind:key="themeId"
        class="theme-item"
      >
        <div
          v-bind:class="{
            'theme-back': true,
            'theme-back-active': theme === themeId,
          }"
        >
          <div class="theme">
            <div
              v-bind:class="{
                circle: true,
                leftBorder: !isReadable(themesByName[themeId].brandColor),
                rightBorder: !isReadable(themesByName[themeId].primaryColor),
              }"
            >
              <div class="border"></div>
              <div
                class="half left"
                v-bind:style="{
                  background: themesByName[themeId].brandColor,
                }"
              />
              <div
                class="half right"
                v-bind:style="{
                  background: themesByName[themeId].primaryColor,
                }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isPresentation"
      :class="{ list: true, 'list--presentation': isPresentation }"
    >
      <div
        @click="() => selectTheme(themeId)"
        v-for="themeId in this.filteredThemes.slice(7, 14)"
        v-bind:key="themeId"
        class="theme-item"
      >
        <div
          v-bind:class="{
            'theme-back': true,
            'theme-back-active': theme === themeId,
          }"
        >
          <div class="theme">
            <div
              v-bind:class="{
                circle: true,
                leftBorder: !isReadable(themesByName[themeId].brandColor),
                rightBorder: !isReadable(themesByName[themeId].primaryColor),
              }"
            >
              <div class="border"></div>
              <div
                class="half left"
                v-bind:style="{
                  background: themesByName[themeId].brandColor,
                }"
              />
              <div
                class="half right"
                v-bind:style="{
                  background: themesByName[themeId].primaryColor,
                }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isPresentation"
      :class="{ list: true, 'list--presentation': isPresentation }"
    >
      <div
        @click="() => selectTheme(themeId)"
        v-for="themeId in this.filteredThemes.slice(14)"
        v-bind:key="themeId"
        class="theme-item"
      >
        <div
          v-bind:class="{
            'theme-back': true,
            'theme-back-active': theme === themeId,
          }"
        >
          <div class="theme">
            <div
              v-bind:class="{
                circle: true,
                leftBorder: !isReadable(themesByName[themeId].brandColor),
                rightBorder: !isReadable(themesByName[themeId].primaryColor),
              }"
            >
              <div class="border"></div>
              <div
                class="half left"
                v-bind:style="{
                  background: themesByName[themeId].brandColor,
                }"
              />
              <div
                class="half right"
                v-bind:style="{
                  background: themesByName[themeId].primaryColor,
                }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import tinycolor from "tinycolor2";
import uniq from "../helpers/uniq";
import LayoutItem from "./LayoutItem";
import Loader from "./Loader";
import QUERY_PARAMS from "../constants/queryParams";

export default {
  name: "themeSelect",
  components: {
    LayoutItem,
    Loader,
  },
  data() {
    return {
      searchQuery: "",
      activeLayout: null,
      layouts: [
        { type: "tile", label: "Tile" },
        { type: "sidebar", label: "Sidebar" },
        { type: "classic", label: "Classic" },
      ],
    };
  },
  watch: {
    theme() {
      this.setActiveLayout();
    },
    themesByName() {
      this.setActiveLayout();
    },
  },
  mounted() {
    this.setActiveLayout();
  },
  computed: {
    ...mapState("auth", ["theme"]),
    ...mapState("app", ["isPresentation", "themes", "themesLoading"]),
    filteredThemes() {
      const { themes, isPresentation } = this.$store.state.app;
      const themeNames = themes.map((theme) => theme.title);

      if (isPresentation) {
        return uniq(
          themeNames.map((themeName) =>
            themeName.split("-").slice(0, -1).join("-")
          )
        );
      }

      const themesByName = themeNames.filter((n) =>
        this.searchQuery
          ? n.includes(this.searchQuery)
          : themes.find((theme) => theme.title === n).layout ===
            this.activeLayout
      );

      // set default themes as first theme
      const defaultThemes = [];
      if (themesByName.includes("default")) defaultThemes.push("default");
      if (themesByName.includes("default-table"))
        defaultThemes.push("default-table");

      const rest = themesByName.filter(
        (name) => name !== "default" && name !== "default-table"
      );

      return [...defaultThemes, ...rest];
    },
    themesByName() {
      return this.themes.reduce((acc, theme) => {
        const title = this.isPresentation
          ? theme.title.split("-").slice(0, -1).join("-")
          : theme.title;
        acc[title] = theme;
        return acc;
      }, {});
    },
  },
  methods: {
    setActiveLayout() {
      const theme = this.themesByName[this.theme];
      this.activeLayout = theme && theme.layout;
    },
    onLayoutClick(layout) {
      this.activeLayout = layout;
    },
    isReadable(color) {
      return tinycolor.readability("#fff", color) > 1.2;
    },
    selectTheme(theme) {
      const { [QUERY_PARAMS.THEME_NAME]: themeName, ...query } =
        this.$route.query;

      if (themeName) this.$router.replace({ query });

      this.$store.dispatch("auth/selectTheme", { theme });
    },
    clearSearch() {
      this.searchQuery = "";
    },
  },
};
</script>

<style scoped>
.theme-select {
  padding-top: 18px;
}
.theme-content {
  display: flex;
  flex-direction: column;
}
.theme-select__layouts {
  display: flex;
  justify-content: center;
  padding-bottom: 24px;
  border-bottom: 1px solid #ebebeb;
}
.theme-select__layout {
  margin: 0 12px;
}
.theme-search {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 0 16px;
}
.theme-search__input {
  height: 30px;
  width: 300px;
  font-size: 16px;
  font-weight: 600;
  background: none;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  padding: 0 8px;
  background: #ffffff;
}
.theme-search__clear {
  margin-left: 12px;
  text-decoration: underline;
  cursor: pointer;
}
.theme-search__clear:hover {
  text-decoration: none;
}
.list {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  overflow-y: auto;
  width: 700px;
  height: 300px;
  max-height: calc(100% - 70px);
  margin-top: 24px;
}

.list--presentation {
  width: 384px;
  height: 100%;
  flex-wrap: nowrap;
  justify-content: space-between;
  box-sizing: border-box;
}
.list--presentation .theme-item {
  flex: 0;
  padding-left: 0;
  padding-right: 0;
}

.theme-item {
  display: flex;
  align-items: center;
  flex: 0 0 33.333%;
  padding: 8px 36px 8px 36px;
  box-sizing: border-box;
}
.theme-item:hover {
  background: rgba(142, 152, 157, 0.1);
  cursor: pointer;
}
.list--presentation .theme-item:hover {
  background: transparent;
  cursor: default;
}
.theme-name {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  margin-left: 12px;
}
.theme-name-label {
  height: 16px;
  box-sizing: border-box;
  padding: 0 3px;
  background: limegreen;
  border-radius: 4px;
  color: #ffffff;
  margin-left: 4px;
  font-size: 10px;
}
.theme-back {
  width: 32px;
  min-width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #fff;
  border: 2px solid transparent;
  transition: border-color 0.5s;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 5px 1px rgba(0, 0, 0, 0.05);
}
.theme-back-active {
  border-color: #0821ff;
}
.theme {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 600;
  cursor: pointer;
}
.circle {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}
.half {
  position: absolute;
  top: 0;
  z-index: 1;
  width: 12px;
  height: 24px;
  background: #9e978e;
  display: inline-block;
}
.border {
  position: absolute;
  border-radius: 50%;
  z-index: 0;
  width: 100%;
  height: 100%;
  background-color: #d2d2d2;
}
.left {
  left: 0;
  border-bottom-left-radius: 12px;
  border-top-left-radius: 12px;
}
.right {
  right: 0;
  border-bottom-right-radius: 12px;
  border-top-right-radius: 12px;
}
.rightBorder .right {
  top: 1px;
  right: 1px;
  width: 12px;
  height: 22px;
  z-index: 0;
}
.leftBorder .left {
  top: 1px;
  left: 1px;
  width: 12px;
  height: 22px;
  z-index: 0;
}
@media (hover: hover) {
  .vertical .theme-item:hover .theme-back,
  .theme-back:hover {
    border-color: rgba(8, 33, 255, 0.3);
  }
}
@media (max-width: 730px) {
  .theme-item {
    flex: 0 0 100%;
    padding-left: 0;
    padding-right: 0;
  }
  .list {
    width: 320px;
    max-width: 100%;
  }
  .list--presentation .theme-item {
    justify-content: center;
  }
  .list--presentation .theme-item:first-of-type,
  .list--presentation .theme-item:nth-of-type(4n + 1) {
    justify-content: flex-start;
  }
  .list--presentation .theme-item:nth-of-type(4n) {
    justify-content: flex-end;
  }
}
@media (max-width: 600px) {
  .list {
    max-height: 380px;
    box-sizing: border-box;
    width: 100%;
  }
  .list--presentation {
    max-width: initial;
  }
  .theme-select__layout {
    margin: 0 6px;
  }
}
</style>
